import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useSEO } from '@/hooks/useSEO';

const faqs = [
  {
    category: 'ACHC Accreditation Basics',
    questions: [
      {
        q: 'What is ACHC accreditation and why does my home health agency need it?',
        a: 'ACHC (Accreditation Commission for Health Care) is a nationally recognized accreditation organization with CMS deeming authority. ACHC accreditation demonstrates that your agency meets rigorous quality and compliance standards. It is required or preferred by many payers, opens doors to more referral sources, and signals to patients and partners that your agency operates at the highest level of quality. For Medicare-certified agencies, ACHC accreditation can replace the standard state survey process.',
      },
      {
        q: 'How long does the ACHC accreditation process take?',
        a: 'The timeline varies depending on your agency\'s current state of readiness. For agencies starting from scratch, the process typically takes 6 to 12 months. Agencies that already have strong policies, procedures, and operational systems in place may achieve accreditation in as little as 3 to 6 months. Working with an ACHC Certified Consultant like Dr. Villarreal can significantly accelerate the process by eliminating common delays and ensuring you are fully prepared before applying.',
      },
      {
        q: 'What is the difference between ACHC accreditation and state licensure?',
        a: 'State licensure is a legal requirement to operate a home health agency in your state. ACHC accreditation is a voluntary quality certification that demonstrates your agency meets nationally recognized standards. However, because ACHC has CMS deeming authority, ACHC-accredited agencies are deemed to meet Medicare Conditions of Participation, which can replace the standard state survey for Medicare certification purposes.',
      },
      {
        q: 'How much does ACHC accreditation cost?',
        a: 'ACHC accreditation fees vary based on your agency\'s size, services, and location. In addition to ACHC\'s direct fees, you should budget for consulting support, policy development, staff training, and any operational improvements needed to meet standards. Investing in expert consulting upfront typically saves agencies significant time and money by avoiding costly delays, re-surveys, and compliance failures.',
      },
      {
        q: 'How long is ACHC accreditation valid?',
        a: 'ACHC accreditation is typically granted for a three-year period. During this time, your agency must maintain continuous compliance with all applicable standards. ACHC may conduct unannounced surveys at any time. At the end of the three-year period, your agency must undergo a renewal survey to maintain accreditation.',
      },
    ],
  },
  {
    category: 'Working With Advantixx',
    questions: [
      {
        q: 'What services does Advantixx provide for ACHC accreditation?',
        a: 'Advantixx provides comprehensive ACHC accreditation consulting services including: initial readiness assessments and gap analysis, policy and procedure development, mock surveys and readiness evaluations, compliance audits, staff training and education, ongoing compliance support, and full operational system setup. We guide agencies through every phase of the accreditation process from start to finish.',
      },
      {
        q: 'Who is Dr. Osmel Villarreal and what makes him qualified?',
        a: 'Dr. Osmel Villarreal holds a Doctor of Business Administration (DBA), MBA, and MS. He is an ACHC Certified Consultant with real-world experience opening and managing home health agencies, home care agencies, and medical practices. His doctoral capstone research focused specifically on home health operational efficiency and compliance — giving him a unique combination of academic expertise and practical experience that most consultants simply do not have.',
      },
      {
        q: 'Does Advantixx work with new agencies or only established ones?',
        a: 'Advantixx works with agencies at every stage — from brand new startups seeking their first ACHC accreditation to established agencies preparing for renewal surveys or addressing compliance gaps. Whether you are launching a new home health agency or need to strengthen an existing one, we have the expertise and systems to help you succeed.',
      },
      {
        q: 'How does the consulting process work?',
        a: 'We begin with a free consultation to understand your agency\'s current situation, goals, and timeline. From there, we conduct a comprehensive readiness assessment to identify gaps and develop a customized accreditation roadmap. We then work alongside your team through each phase — policy development, staff training, mock surveys, and final preparation — until your agency is fully ready for the ACHC survey.',
      },
      {
        q: 'Do you provide ongoing support after accreditation is achieved?',
        a: 'Yes. Maintaining accreditation requires continuous compliance, and Advantixx offers ongoing compliance support packages to help agencies stay survey-ready between accreditation cycles. This includes periodic compliance audits, policy updates, staff training refreshers, and regulatory monitoring to keep your agency ahead of any changes in ACHC standards or CMS requirements.',
      },
    ],
  },
  {
    category: 'Compliance & Survey Preparation',
    questions: [
      {
        q: 'What does ACHC look for during a survey?',
        a: 'ACHC surveyors evaluate your agency across multiple domains including: clinical record documentation, patient rights and safety, infection control practices, staff qualifications and training, quality assurance and performance improvement (QAPI), emergency preparedness, administrative and operational systems, and leadership and governance. They review documents, interview staff at all levels, and observe operations to assess compliance with all applicable standards.',
      },
      {
        q: 'What is a mock survey and do I really need one?',
        a: 'A mock survey is a simulated ACHC survey conducted before the real one. It evaluates your agency against the same standards ACHC surveyors use, identifies gaps, and gives your team practice responding to surveyor questions. We strongly recommend mock surveys for all agencies — they are one of the most effective tools for ensuring accreditation success and preventing costly surprises during the actual survey.',
      },
      {
        q: 'What happens if my agency fails the ACHC survey?',
        a: 'If deficiencies are found during the ACHC survey, your agency will be required to submit a Plan of Correction (POC) addressing each finding. ACHC will review your POC and may conduct a follow-up survey to verify corrections. Working with an experienced consultant before and after the survey significantly reduces the risk of survey failure and helps agencies respond effectively to any findings.',
      },
      {
        q: 'How do I know if my policies and procedures meet ACHC standards?',
        a: 'The only reliable way to know is to have your policies reviewed against current ACHC standards by someone who knows them thoroughly. Advantixx conducts comprehensive policy and procedure reviews as part of our readiness assessment services. We identify gaps, recommend updates, and help develop new policies where needed to ensure full compliance with all applicable ACHC standards.',
      },
    ],
  },
];

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/faq#webpage`,
  url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/faq`,
  name: 'ACHC Accreditation FAQ | Advantixx Home Health Consultants',
  description: 'Frequently asked questions about ACHC accreditation, home health compliance, and working with Advantixx — answered by our ACHC Certified Consultant.',
  isPartOf: { '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#website` },
};

export default function FaqPage() {
  const [openItem, setOpenItem] = useState<string | null>(null);

  useSEO({
    title: 'ACHC Accreditation FAQ | Advantixx Home Health Consultants',
    description: 'Frequently asked questions about ACHC accreditation, home health compliance, and working with Advantixx — answered by our ACHC Certified Consultant.',
    keywords: 'ACHC accreditation FAQ, home health compliance questions, ACHC survey questions, accreditation process FAQ',
    canonical: '/faq',
  });

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-faq-webpage';
    script.text = JSON.stringify(webPageSchema);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('schema-faq-webpage');
      if (el) el.remove();
    };
  }, []);

  const toggle = (key: string) => {
    setOpenItem(openItem === key ? null : key);
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] font-['Montserrat',sans-serif]">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-40 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/c754ad020694149832122fadd3b002a9.jpg"
            alt="FAQ background"
            className="w-full h-full object-cover object-top opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0d0d0d]/70 to-[#0d0d0d]"></div>
        </div>
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 text-center">
          <span className="inline-block text-[#d4af37] text-xs uppercase tracking-[4px] font-semibold mb-5">Knowledge Base</span>
          <h1 className="text-4xl md:text-6xl font-black text-white leading-tight mb-6">
            Frequently Asked<br />
            <span className="text-[#d4af37]">Questions</span>
          </h1>
          <p className="text-[#aaa] text-lg max-w-2xl mx-auto leading-relaxed">
            Everything you need to know about ACHC accreditation, home health compliance, and working with Advantixx — answered by our ACHC Certified Consultant.
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 pb-24">
        {faqs.map((section, si) => (
          <div key={si} className="mb-14">
            <div className="flex items-center gap-4 mb-8">
              <div className="h-px flex-1 bg-[#1e1e1e]"></div>
              <h2 className="text-[#d4af37] text-xs uppercase tracking-[3px] font-bold whitespace-nowrap">{section.category}</h2>
              <div className="h-px flex-1 bg-[#1e1e1e]"></div>
            </div>
            <div className="flex flex-col gap-3">
              {section.questions.map((item, qi) => {
                const key = `${si}-${qi}`;
                const isOpen = openItem === key;
                return (
                  <div
                    key={qi}
                    className={`border rounded-xl overflow-hidden transition-all duration-300 ${
                      isOpen ? 'border-[#d4af37]/40 bg-[#111]' : 'border-[#1e1e1e] bg-[#0f0f0f] hover:border-[#2a2a2a]'
                    }`}
                  >
                    <button
                      onClick={() => toggle(key)}
                      className="w-full flex items-start justify-between gap-4 px-6 py-5 text-left cursor-pointer"
                    >
                      <span className={`font-semibold text-sm leading-snug transition-colors duration-200 ${isOpen ? 'text-[#d4af37]' : 'text-white'}`}>
                        {item.q}
                      </span>
                      <div className={`w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5 rounded-full border transition-all duration-300 ${
                        isOpen ? 'border-[#d4af37] bg-[#d4af37]/10 rotate-45' : 'border-[#333]'
                      }`}>
                        <i className={`ri-add-line text-sm ${isOpen ? 'text-[#d4af37]' : 'text-[#666]'}`}></i>
                      </div>
                    </button>
                    {isOpen && (
                      <div className="px-6 pb-6">
                        <div className="h-px bg-[#d4af37]/15 mb-5"></div>
                        <p className="text-[#888] text-sm leading-relaxed">{item.a}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Still have questions */}
        <div className="bg-[#111] border border-[#d4af37]/20 rounded-2xl p-10 text-center mt-10">
          <div className="w-14 h-14 flex items-center justify-center mx-auto mb-6 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30">
            <i className="ri-question-answer-line text-[#d4af37] text-2xl"></i>
          </div>
          <h3 className="text-white font-black text-2xl mb-3">Still Have Questions?</h3>
          <p className="text-[#888] text-sm mb-8 max-w-lg mx-auto leading-relaxed">
            Our ACHC Certified Consultant is ready to answer your specific questions and help you understand exactly what your agency needs to achieve accreditation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/services"
              className="inline-flex items-center justify-center gap-2 bg-[#d4af37] text-[#0d0d0d] font-bold text-sm px-8 py-3.5 rounded-full hover:bg-[#f4c430] transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Book a Free Consultation
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/blog"
              className="inline-flex items-center justify-center gap-2 border border-[#d4af37]/40 text-[#d4af37] font-bold text-sm px-8 py-3.5 rounded-full hover:bg-[#d4af37]/10 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Read Our Blog
              <i className="ri-article-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Schema.org FAQPage structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.flatMap((section) =>
              section.questions.map((item) => ({
                '@type': 'Question',
                name: item.q,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: item.a,
                },
              }))
            ),
          }),
        }}
      />

      <Footer />
    </div>
  );
}
